import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import classNames from 'classnames';

import logo from '../../assets/img/content/logo-new.webp';
import { SecretCodeForm } from './SecretCodeForm';
import { AuthForm } from './AuthForm';

import style from './style.module.css';

const { string, bool, func } = PropTypes;

const ERROR_401 = '401 Unauthorized';

/**
 * Stateless component for login form.
 *
 * @param props {Object}.
 * @param props.onSubmit {Function} Handle submit form event.
 * @param props.onGoogle2FAAuth {Function} Handle onGoogle2FAAuth form event.
 * @param props.error {String} Validate & API error.
 * @param props.caption {React.ReactElement} Additional caption.
 * @param props.className {String} Additional root className.
 * @param props.isFetching {boolean} Is in fetching API state or not.
 * @param props.google2FaStatus {Map} Google 2FA status.
 * @param props.google2FaSecret {Map} Google 2FA secret.
 * @returns {JSX.Element} Form component.
 */
const LoginFormComponent = ({
  caption,
  className,
  error,
  isFetching,
  google2FaStatus,
  google2FaSecret,
  onSubmit,
  onGoogle2FAAuth,
}) => {
  let errorContent = error ? <p className={style.error}>{error}</p> : null;

  if (error === ERROR_401) {
    errorContent = (
      <p className={style.error}>Your authorization token has expired. Enter username and password again.</p>
    );
  }

  return (
    <div className={classNames(style.host, className)}>
      <div className={style.brand}>
        <img alt="Harvey & Company" className={style.logo} src={logo} />
      </div>
      <div className={style.body}>
        {isSecretCodeForm(google2FaStatus, google2FaSecret, error) ? (
          <SecretCodeForm
            errorContent={errorContent}
            google2FaSecret={google2FaSecret}
            isFetching={isFetching}
            onSubmit={onGoogle2FAAuth}
          />
        ) : (
          <AuthForm caption={caption} errorContent={errorContent} isFetching={isFetching} onSubmit={onSubmit} />
        )}
      </div>
    </div>
  );
};

const isSecretCodeForm = (google2FaStatus, google2FaSecret, error) => {
  if (error === ERROR_401) {
    return false;
  }

  if (google2FaStatus && !google2FaStatus.get('isGoogle2FAPassed')) {
    return true;
  }

  if (google2FaStatus && !google2FaStatus.get('hasGoogle2FASecret')) {
    return true;
  }

  if (google2FaSecret) {
    return true;
  }

  return false;
};

LoginFormComponent.propTypes = {
  className: string,
  error: string.isRequired,
  google2FaSecret: PropTypes.instanceOf(Map),
  google2FaStatus: PropTypes.instanceOf(Map),
  isFetching: bool.isRequired,
  onGoogle2FAAuth: func.isRequired,
  onSubmit: func.isRequired,
};

export const LoginForm = memo(LoginFormComponent);
